import { useState } from "react";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { Loader2, Lock, UserRound, ShieldCheck, Factory, KeyRound, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const HERO = "/bg-workshop.jpg";

export default function Login() {
  const { login, lang, setLang } = useApp();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [forgot, setForgot] = useState(false);
  const [forgotUser, setForgotUser] = useState("");
  const [forgotNote, setForgotNote] = useState("");
  const [forgotDone, setForgotDone] = useState("");
  const { branding } = useApp();

  const submitForgot = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-username", { username: forgotUser, note: forgotNote });
      setForgotDone(data.message);
    } catch (ex) { setErr(fmtErr(ex)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(username, password);
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <img src={HERO} alt="Workshop fabrikasi HONGLU" className="absolute inset-0 h-full w-full object-cover" data-testid="login-background" />
      <div className="absolute inset-0 bg-[#07090E]/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07090E]/90 via-[#07090E]/40 to-[#07090E]/80" />
      {/* brand panel */}
      <div className="relative hidden lg:block overflow-hidden">
        <div className="scanlines absolute inset-0" />
        <div className="relative flex h-full flex-col justify-between p-10">
          <Logo />
          <div>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [.22, 1, .36, 1] }}>
              <div className="mb-3 inline-flex items-center gap-2 rounded border border-sky-500/30 bg-sky-500/10 px-3 py-1 text-[11px] font-mono tracking-[0.25em] text-sky-300 uppercase">
                <Factory className="h-3.5 w-3.5" /> Fabrication & Delivery Control
              </div>
              <h1 className="font-display text-5xl font-black uppercase leading-[1.02] tracking-tight text-white">
                HONGLU<br />FABRIKASI<br />
                <span className="bg-gradient-to-r from-sky-400 to-amber-400 bg-clip-text text-transparent">SISTEM</span>
              </h1>
              <p className="cn mt-3 text-lg text-slate-300">加工与交付控制系统 · 宏睿钢印印尼有限公司</p>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
                Kendalikan target fabrikasi, QC, STB, dan delivery tonase proyek baja dalam satu ruang kendali terpadu.
              </p>
            </motion.div>
            {branding?.tagline && <div className="mt-8 inline-flex items-center gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-1 font-display text-xs tracking-[0.2em] text-amber-300 uppercase" data-testid="login-tagline">{branding.tagline}</div>}
          </div>
          <div className="text-[11px] text-slate-600">© 2026 PT HONGLU BAJA INDONESIA 宏睿钢印印尼有限公司</div>
        </div>
      </div>

      {/* form panel */}
      <div className="relative flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [.22, 1, .36, 1] }} className="w-full max-w-sm rounded-xl border border-slate-800/80 bg-[#0B1220]/80 p-7 shadow-2xl backdrop-blur-xl" data-testid="login-card">
          <div className="mb-8 flex items-center justify-between lg:hidden"><Logo /></div>
          <div className="flex justify-end">
            <button onClick={() => setLang(lang === "bi" ? "id" : lang === "id" ? "zh" : "bi")}
              className="mb-4 rounded border border-slate-800 px-2.5 py-1 font-mono text-[11px] text-slate-400 hover:border-sky-500/50" data-testid="login-lang-toggle-button">
              {lang === "bi" ? "ID + 中文" : lang === "id" ? "ID" : "中文"}
            </button>
          </div>
          {forgot ? (
            <div data-testid="forgot-panel">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-amber-500/10 border border-amber-500/30 mb-4"><KeyRound className="h-6 w-6 text-amber-400" /></div>
              <h2 className="font-display text-xl font-bold text-slate-100">Lupa Password <span className="cn text-sm font-normal text-slate-500">忘记密码</span></h2>
              <p className="mt-1 text-xs text-slate-400">Masukkan username Anda. Admin akan memverifikasi dan memberi password sementara; Anda wajib menggantinya saat login.</p>
              {forgotDone ? (
                <div className="mt-4 rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-xs text-emerald-300" data-testid="forgot-success">{forgotDone}</div>
              ) : (
                <form onSubmit={submitForgot} className="mt-5 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-400">Username</Label>
                    <Input value={forgotUser} onChange={(e) => setForgotUser(e.target.value.toLowerCase())} required autoCapitalize="none" placeholder="username" className="border-slate-800 bg-slate-900/70" data-testid="forgot-username-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-400">Catatan untuk admin (opsional)</Label>
                    <Input value={forgotNote} onChange={(e) => setForgotNote(e.target.value)} placeholder="mis. HP hilang, lupa password" className="border-slate-800 bg-slate-900/70" data-testid="forgot-note-input" />
                  </div>
                  {err && <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300" data-testid="forgot-error">{err}</div>}
                  <Button type="submit" disabled={busy} className="w-full bg-amber-600 text-[#0B1220] hover:bg-amber-500" data-testid="forgot-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kirim Permintaan ke Admin</Button>
                </form>
              )}
              <button type="button" onClick={() => { setForgot(false); setForgotDone(""); setErr(""); }} className="mt-4 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200" data-testid="forgot-back-button"><ArrowLeft className="h-3.5 w-3.5" /> Kembali ke login</button>
            </div>
          ) : (<>
          <div className="flex h-12 w-12 items-center justify-center rounded bg-sky-500/10 border border-sky-500/30 mb-4">
            <ShieldCheck className="h-6 w-6 text-sky-400" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-100">Masuk Sistem <span className="cn text-base text-slate-400 font-normal">登录</span></h2>
          <p className="mt-1 text-sm text-slate-500">Gunakan username dan password terdaftar.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs text-slate-400">Username <span className="cn text-slate-500">用户名</span></Label>
              <div className="relative mt-1.5">
                <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input value={username} onChange={(e) => setUsername(e.target.value)} type="text" autoComplete="username" autoCapitalize="none" required placeholder="username"
                  className="border-slate-800 bg-slate-900/70 pl-9" data-testid="login-username-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="••••••••"
                  className="border-slate-800 bg-slate-900/70 pl-9" data-testid="login-password-input" />
              </div>
            </div>
            {err && <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300" data-testid="login-error">{err}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-sky-600 hover:bg-sky-500" data-testid="login-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Masuk Sistem · 登录
            </Button>
          </form>

          <div className="mt-3 flex justify-end">
            <button type="button" onClick={() => { setForgot(true); setErr(""); }} className="text-xs text-sky-400 hover:text-sky-300" data-testid="forgot-link">Lupa password? <span className="cn text-slate-500">忘记密码</span></button>
          </div>
          <button onClick={() => { setUsername("admin"); setPassword("Honglu2026!"); }}
            className="mt-4 w-full rounded border border-dashed border-slate-700 px-3 py-2 text-left text-[11px] text-slate-500 hover:border-sky-500/40 hover:text-slate-300 transition-colors" data-testid="login-demo-hint">
            Demo admin — klik untuk isi otomatis: <span className="font-mono text-slate-400">admin / Honglu2026!</span>
          </button>
          </>)}
        </motion.div>
      </div>
    </div>
  );
}